import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { ActionsObservable } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, map, switchMap } from 'rxjs/operators';
import {
  AsyncActionCreatorBuilder,
  isActionOf,
  PayloadAction,
  RootAction,
  RootState,
  RootStateKey,
} from 'typesafe-actions';

import { SortingProps } from '@block-explorer/ui';

import { FilterProps, PaginationProps } from '../components';
import { usePaginationState } from '../router/navigation';
import {
  PaginatedResponse,
  PaginationParams,
  RequestParams,
} from '../services';

import { add, isAfter } from 'date-fns';

declare global {
  interface Window {
    __REDUX_DEVTOOLS_EXTENSION_COMPOSE__: any;
  }
}

export const composeEnhancers =
  (process.env.NODE_ENV === 'development' &&
    window &&
    window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__) ||
  compose;

export const DEFAULT_CLOUD_PAGINATION_PARAMS: Partial<PaginationParams> = {
  page: 1,
  page_size: 10,
};

export type PaginatedReducerState<Entity> = {
  loading: boolean;
  error: string | null;
  byHash: {
    loading: boolean;
    error: string | null;
    items: Record<string, Entity>;
  };
  paginatedItems: Entity[];
  totalItemCount: number;
  totalPageCount: number;
};

/**
 * @param action$
 * @param actionCreatorAsync
 * @param stream
 */
export const createPaginatedGetEpic = <Params extends RequestParams>(
  action$: ActionsObservable<RootAction>,
  actionCreatorAsync: AsyncActionCreatorBuilder<any, any, any>,
  stream: (
    params: Partial<PaginationParams<any>> & Params
  ) => Observable<PaginatedResponse<any>>
) =>
  action$.pipe(
    filter(isActionOf(actionCreatorAsync.request)),
    switchMap((action: PayloadAction<any, any>) =>
      from(
        stream({
          ...DEFAULT_CLOUD_PAGINATION_PARAMS,
          ...action.payload,
        })
      ).pipe(
        map((res: any) => {
          if ('error' in res) {
            return actionCreatorAsync.failure(res.error.message);
          }
          return actionCreatorAsync.success(res);
        }),
        catchError((err: Error) => of(actionCreatorAsync.failure(err.message)))
      )
    )
  );

export const createGetEpic = <Params extends RequestParams>(
  action$: ActionsObservable<RootAction>,
  actionCreatorAsync: AsyncActionCreatorBuilder<any, any, any>,
  stream: (params: Params) => Observable<any>
) =>
  action$.pipe(
    filter(isActionOf(actionCreatorAsync.request)),
    switchMap((action: PayloadAction<any, any>) =>
      from(
        stream({
          ...action.payload,
        })
      ).pipe(
        map((res) => {
          if ('error' in res) {
            return actionCreatorAsync.failure(res.error.message);
          }
          return actionCreatorAsync.success(res);
        }),
        catchError((err: Error) => of(actionCreatorAsync.failure(err.message)))
      )
    )
  );

type GetPaginatedKeys<T extends RootStateKey> = Exclude<
  T extends (RootState[T] extends PaginatedReducerState<any> ? T : void)
    ? T
    : void,
  void
>;

export const createPaginatedGetReducerHook =
  <
    Params extends RequestParams,
    ReducerKey extends GetPaginatedKeys<RootStateKey>,
  >(
    paginationStateKey: string,
    reducerKey: ReducerKey,
    actionCreatorAsync: AsyncActionCreatorBuilder<any, any, any>
  ) =>
  (
    customParams: (Params & Partial<PaginationParams>) | null
  ): {
    loading: boolean;
    error: string | null;
    data: RootState[ReducerKey]['paginatedItems'];
    paginationProps: PaginationProps;
    sortingProps: SortingProps;
    filterProps: FilterProps;
  } => {
    const loading = useSelector(
      (rootState: RootState) => rootState[reducerKey].loading
    );
    const error = useSelector(
      (rootState: RootState) => rootState[reducerKey].error
    );
    const paginatedItems = useSelector(
      (rootState: RootState) => rootState[reducerKey].paginatedItems
    );
    const totalItemCount = useSelector(
      (rootState: RootState) => rootState[reducerKey].totalItemCount
    );
    const totalPageCount = useSelector(
      (rootState: RootState) => rootState[reducerKey].totalPageCount
    );

    const {
      currentPage,
      perPage,
      setCurrentPage,
      setPerPage,
      orderBy,
      orderDirection,
      setOrder,
      filters,
      setFilters,
    } = usePaginationState(paginationStateKey, customParams?.defaultPageSize);

    const paginationProps: PaginationProps = {
      itemCount: totalItemCount,
      pageCount: totalPageCount || 0,
      perPage: perPage,
      currentPage: currentPage,
      setPerPage: setPerPage,
      setCurrentPage: (page: number) => {
        if (page > 0 && page <= totalItemCount) {
          setCurrentPage(page);
        }
      },
    };

    const sortingProps: SortingProps = {
      orderBy,
      orderDirection,
      setOrder,
    };

    const filterProps: FilterProps = {
      filters,
      setFilters,
    };

    const dispatch = useDispatch();
    useEffect(() => {
      if (customParams !== undefined && customParams !== null) {
        const nonNullableFilters = Object.fromEntries(
          Object.entries(filterProps?.filters || {}).filter(
            ([_, value]) => value != null && value !== ''
          )
        );
        dispatch(
          actionCreatorAsync.request({
            page_size: paginationProps.perPage,
            page: paginationProps.currentPage,
            order_by: sortingProps.orderBy,
            order_direction: sortingProps.orderDirection,
            ...nonNullableFilters,
            ...customParams,
          })
        );
      }

      // TODO: setTimeout to get pending details
      return () => {
        // clear interval
      };
    }, [
      paginationProps.perPage,
      paginationProps.currentPage,
      sortingProps.orderBy,
      sortingProps.orderDirection,
      filterProps.filters,
      JSON.stringify(customParams),
    ]);

    return {
      data: paginatedItems,
      error,
      loading,
      paginationProps,
      sortingProps,
      filterProps,
    };
  };

export type GetReducerHook<ReducerKey extends GetPaginatedKeys<RootStateKey>> =
  {
    loading: boolean;
    error: string | null;
    data: RootState[ReducerKey]['byHash']['items'][string] | null;
  };

export const createGetByHashReducerHook =
  <
    Params extends RequestParams,
    ReducerKey extends GetPaginatedKeys<RootStateKey>,
  >(
    reducerKey: ReducerKey,
    actionCreatorAsync: AsyncActionCreatorBuilder<any, any, any>
  ) =>
  (
    id?: string | null,
    customParams?: Params & Partial<PaginationParams>
  ): {
    loading: boolean;
    error: string | null;
    data: RootState[ReducerKey]['byHash']['items'][string] | null;
  } => {
    const loading = useSelector(
      (rootState: Pick<RootState, ReducerKey>) =>
        rootState[reducerKey].byHash.loading
    );
    const error = useSelector(
      (rootState: Pick<RootState, ReducerKey>) =>
        rootState[reducerKey].byHash.error
    );
    const byHash = useSelector(
      (rootState: Pick<RootState, ReducerKey>) =>
        rootState[reducerKey].byHash.items
    );

    // try load data by id from redux
    const reduxData =
      id == null
        ? null
        : (byHash[id] as RootState[ReducerKey]['byHash']['items'][string]) ||
          null;

    const dispatch = useDispatch();

    // effect responsible for refreshing redux data on load in the background
    useEffect(() => {
      // dispatch request only when id is not null
      if (id) {
        dispatch(actionCreatorAsync.request({ hash: id, ...customParams }));
      }

      return () => {};
    }, [id, JSON.stringify(customParams)]);

    // if reduxData exists do not show loading or error
    return {
      data: reduxData,
      error: reduxData != null ? null : error,
      loading: reduxData != null ? false : loading,
    };
  };

export const createGetReducerHook =
  <Params extends RequestParams, ReducerKey extends RootStateKey>(
    reducerKey: ReducerKey,
    actionCreatorAsync: AsyncActionCreatorBuilder<any, any, any>,
    storeDataKey: string
  ) =>
  (): {
    loading: boolean;
    error: string | null;
    data: RootState[ReducerKey][any] | null;
  } => {
    const loading = useSelector(
      (rootState: Pick<RootState[ReducerKey], any>) =>
        rootState[reducerKey].loading
    );
    const error = useSelector(
      (rootState: Pick<RootState[ReducerKey], any>) =>
        rootState[reducerKey].error
    );
    const data = useSelector(
      (rootState: Pick<RootState[ReducerKey], any>) =>
        rootState[reducerKey][storeDataKey]
    );

    const dispatch = useDispatch();

    useEffect(() => {
      if (!data) {
        dispatch(actionCreatorAsync.request());
      }

      return () => {};
    }, [data]);

    return {
      data,
      error: data != null ? null : error,
      loading: data != null ? false : loading,
    };
  };

export const isExpired = ({ buildDate, durationDays }) => {
  const endDateInMilliseconds = add(new Date(buildDate), {
    days: durationDays,
  }).getTime();

  const currentDateInMilliseconds = new Date().getTime();

  return currentDateInMilliseconds > endDateInMilliseconds;
};

/**
 * Check if deploy is expired
 * @param timestamp
 * @param ttl
 */
export const isDeployExpired = ({ timestamp }) => {
  const expiredDate = add(new Date(timestamp), {
    seconds: Number(config.multisig_deploy_ttl) / 1000,
  });

  const nowUTC = new Date();

  return isAfter(nowUTC, expiredDate);
};

export const getExpirationPeriod = () => {
  return (Number(config.multisig_deploy_ttl) / (1000 * 60 * 60)).toFixed(2);
};

/**
 * Get expiration date of created multisig deploy
 * @param timestamp
 */
export const getExpirationDate = ({ timestamp }) => {
  return add(new Date(timestamp), {
    seconds: Number(config.multisig_deploy_ttl) / 1000,
  }).toISOString();
};
